import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { SearchX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocale } from "@/hooks/useLocale";
import {
  useGetChatContactsInfiniteQuery,
  useOpenChatDirectMutation,
  useSearchAllChatMessagesInfiniteQuery,
} from "@/services/(my-space)/api.chat";
import type { IChatConversation, IChatMessage } from "@/types/(my-space)/chat.interface";
import { getErrorMessage } from "@/utils/error.utils";
import { ChatAvatar } from "./ChatAvatar";
import { Highlighted } from "./MessageSearch";
import {
  conversationTime,
  conversationTitle,
  messageContentPreview,
  personName,
} from "../_lib/chat-labels";
import { stripMarkdown } from "../_lib/markdown";
import { searchResultText, searchSnippet } from "../_lib/message-search";
import { channelOf } from "../_lib/channel-notices";

/** People shown before "Show all". */
const PEOPLE_PREVIEW = 4;

/**
 * The conversation list while searching, WhatsApp-style: matching chats (by
 * name, as before), people I can message but have no chat with yet, and
 * messages from any of my chats with the match highlighted.
 */
export const ChatSearchResults = ({
  query,
  chats,
  chatsLoading,
  renderChat,
  myUserId,
  onOpenChat,
  onOpenMessage,
}: {
  query: string;
  chats: IChatConversation[];
  chatsLoading: boolean;
  renderChat: (c: IChatConversation) => ReactNode;
  myUserId: number | undefined;
  onOpenChat: (conversationId: number) => void;
  onOpenMessage: (conversationId: number, messageId: number) => void;
}) => {
  const { t } = useTranslation("chat");
  const [allPeople, setAllPeople] = useState(false);
  const peopleQuery = useGetChatContactsInfiniteQuery({ search: query });
  const messagesQuery = useSearchAllChatMessagesInfiniteQuery({ q: query });
  const [openDirect, { isLoading: opening }] = useOpenChatDirectMutation();

  // Someone I already have a direct chat with shows as that chat.
  const people = useMemo(() => {
    const peers = new Set(chats.filter((c) => c.type === "direct").map((c) => c.peer?.id));
    return (peopleQuery.data?.pages.flatMap((p) => p.data) ?? []).filter(
      (u) => u.id !== myUserId && !peers.has(u.id),
    );
  }, [chats, myUserId, peopleQuery.data]);

  const pages = messagesQuery.data?.pages ?? [];
  const messages = pages.flatMap((p) => p.data?.messages ?? []);
  const chatById = useMemo(() => {
    const map = new Map<number, IChatConversation>();
    for (const p of messagesQuery.data?.pages ?? []) {
      for (const c of p.data?.conversations ?? []) map.set(c.id, c);
    }
    return map;
  }, [messagesQuery.data]);

  const startDirect = async (userId: number) => {
    try {
      const c = await openDirect(userId).unwrap();
      onOpenChat(c.id);
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.open")));
    }
  };

  const loading = chatsLoading || peopleQuery.isLoading || messagesQuery.isLoading;
  // A failed message search is said so — never passed off as "no results".
  const messagesFailed = messagesQuery.isError && !messages.length;
  const nothing = !loading && !messagesFailed && !chats.length && !people.length && !messages.length;
  if (nothing) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
        <SearchX className="size-6 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">{t("list.no_results", { query })}</p>
        <p className="text-xs text-muted-foreground">{t("global_search.no_results_hint")}</p>
      </div>
    );
  }

  const shownPeople = allPeople ? people : people.slice(0, PEOPLE_PREVIEW);

  return (
    <div className="flex flex-col pb-3" aria-busy={loading || undefined}>
      {chats.length || chatsLoading ? (
        <Section title={t("global_search.chats")} count={chats.length}>
          {chatsLoading ? <RowsSkeleton rows={2} /> : <ul className="flex flex-col">{chats.map(renderChat)}</ul>}
        </Section>
      ) : null}

      {people.length || peopleQuery.isLoading ? (
        <Section title={t("global_search.people")} count={people.length}>
          {peopleQuery.isLoading ? (
            <RowsSkeleton rows={2} />
          ) : (
            <ul className="flex flex-col px-2">
              {shownPeople.map((u) => {
                const name = personName(t, u);
                return (
                  <li key={u.id}>
                    <button
                      type="button"
                      disabled={opening}
                      onClick={() => void startDirect(u.id)}
                      className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-start hover:bg-muted disabled:opacity-60"
                    >
                      <ChatAvatar name={name} imageUrl={u.profileImage} size="md" />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-medium text-foreground">
                          <Highlighted segments={searchSnippet(name, query, 60)} />
                        </span>
                        {u.position ? (
                          <span className="truncate text-xs text-muted-foreground">{u.position}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {!allPeople && people.length > PEOPLE_PREVIEW ? (
            <div className="px-4 pt-1">
              <Button variant="ghost" size="xs" onClick={() => setAllPeople(true)}>
                {t("global_search.show_all", { count: people.length })}
              </Button>
            </div>
          ) : null}
        </Section>
      ) : null}

      {messagesFailed ? (
        <Section title={t("global_search.messages")} count={0}>
          <div className="flex flex-col items-center gap-2 px-4 py-3 text-center">
            <span className="text-xs text-muted-foreground">{t("search.error")}</span>
            <Button variant="outline" size="xs" onClick={() => void messagesQuery.refetch()}>
              {t("page.try_again")}
            </Button>
          </div>
        </Section>
      ) : null}

      {messages.length || messagesQuery.isLoading ? (
        <Section title={t("global_search.messages")} count={messages.length} more={messagesQuery.hasNextPage}>
          {messagesQuery.isLoading ? (
            <RowsSkeleton rows={3} />
          ) : (
            <ul className="flex flex-col px-2">
              {messages.map((m) => (
                <li key={m.id}>
                  <MessageHit
                    message={m}
                    conversation={chatById.get(m.conversationId)}
                    query={query}
                    myUserId={myUserId}
                    onOpen={() => onOpenMessage(m.conversationId, m.id)}
                  />
                </li>
              ))}
            </ul>
          )}
          {messagesQuery.hasNextPage ? (
            <div className="flex justify-center px-4 pt-1">
              <Button
                variant="ghost"
                size="sm"
                isLoading={messagesQuery.isFetchingNextPage}
                onClick={() => void messagesQuery.fetchNextPage()}
              >
                {t("search.load_more")}
              </Button>
            </div>
          ) : messagesQuery.isError ? (
            <p className="px-4 py-2 text-center text-xs text-muted-foreground">{t("search.error")}</p>
          ) : null}
        </Section>
      ) : null}
    </div>
  );
};

const Section = ({
  title,
  count,
  more,
  children,
}: {
  title: string;
  count: number;
  /** More results exist than are loaded: the count reads "20+". */
  more?: boolean;
  children: ReactNode;
}) => (
  <section className="flex flex-col pt-2">
    <h3 className="flex items-center gap-1.5 px-4 pb-1 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
      {title}
      {count ? (
        <span className="rounded-full bg-muted px-1.5 text-[10px] tabular-nums">
          {count}
          {more ? "+" : ""}
        </span>
      ) : null}
    </h3>
    {children}
  </section>
);

const RowsSkeleton = ({ rows }: { rows: number }) => (
  <div className="flex flex-col gap-1 px-4 py-1">
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex items-center gap-3 py-1.5">
        <Skeleton className="size-9 rounded-full" />
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton className="h-3 w-1/2 rounded" />
          <Skeleton className="h-2.5 w-3/4 rounded" />
        </div>
      </div>
    ))}
  </div>
);

/** A message from any chat: which chat, who wrote it, the match in context. */
const MessageHit = ({
  message: m,
  conversation: c,
  query,
  myUserId,
  onOpen,
}: {
  message: IChatMessage;
  conversation: IChatConversation | undefined;
  query: string;
  myUserId: number | undefined;
  onOpen: () => void;
}) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const chatTitle = c ? conversationTitle(t, c) : personName(t, m.sender);
  const isMine = m.sender?.id === myUserId;
  // In a group, who said it; in a direct chat, only when it was me.
  const who = isMine ? t("search.you") : c && c.type !== "direct" ? personName(t, m.sender) : null;
  const text =
    stripMarkdown(searchResultText(m, query)) ||
    messageContentPreview(t, { ...m, attachmentCount: m.attachments.length });
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-start gap-3 rounded-md px-2 py-2.5 text-start transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-hidden"
    >
      <ChatAvatar
        name={chatTitle}
        imageUrl={c ? (c.type === "direct" ? c.peer?.profileImage : c.imageUrl) : m.sender?.profileImage}
        kind={c?.type}
        channel={channelOf(c)}
        size="md"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{chatTitle}</span>
          <span className="ms-auto shrink-0 text-[11px] text-muted-foreground">
            {conversationTime(t, formatDate, m.createdAt)}
          </span>
        </span>
        <span className="line-clamp-2 text-[13px] break-words text-muted-foreground">
          {who ? <span className="font-medium text-foreground/80">{who}: </span> : null}
          {/* User content keeps its own direction (English in an Arabic UI). */}
          <bdi>
            <Highlighted segments={searchSnippet(text, query)} />
          </bdi>
        </span>
      </span>
    </button>
  );
};
